import { type IncubatorTypeInfo, type Label } from '@/app/services/fertWork';

import { EVEN_PREFERENCE } from './incubatorSelection';

// Client-side port of FertWorkRepository.PlanIncubatorMaps' validation checks (API side is the
// source of truth). Mirrors it closely so the UI can catch "not enough incubators" up front
// instead of only finding out from a 400 on Generate. Keep in sync with the API if that
// algorithm changes.

type Category = 'Standard' | 'Day5' | 'Export';

const isExportCategory = (planCategory?: string): boolean =>
  !!planCategory && planCategory.toLowerCase().startsWith('export');

const categoryFor = (label: Label): Category => {
  if (label.isDay5) return 'Day5';
  if (isExportCategory(label.planCategory)) return 'Export';
  return 'Standard';
};

const speciesFor = (label: Label): string =>
  label.top?.speciesSubfamily ?? label.bottom?.speciesSubfamily ?? 'Bovine';

const unitsNeededFor = (labels: Label[], capacity: number): number => {
  if (labels.length === 0 || capacity <= 0) return 0;
  const bySpecies = new Map<string, number>();
  for (const label of labels) {
    const species = speciesFor(label);
    bySpecies.set(species, (bySpecies.get(species) ?? 0) + 1);
  }
  let units = 0;
  for (const count of bySpecies.values()) {
    units += Math.ceil(count / capacity);
  }
  return units;
};

export type IncubatorPlanValidationResult = {
  error: string | null;
};

export const validateIncubatorPlan = (
  labels: Label[],
  incubatorTypes: IncubatorTypeInfo[],
  counts: Record<string, number>,
  preferenceOrder: string[],
): IncubatorPlanValidationResult => {
  const capacityByType = new Map(
    incubatorTypes.map((it) => [it.name, it.capacity]),
  );

  const activeCounts: Record<string, number> = {};
  for (const [name, count] of Object.entries(counts)) {
    if (count > 0 && capacityByType.has(name)) activeCounts[name] = count;
  }

  const activeTypeNames = Object.keys(activeCounts);
  if (activeTypeNames.length === 0) return { error: null };

  const totalLabelCount = labels.length;
  const isEven =
    preferenceOrder.length === 1 && preferenceOrder[0] === EVEN_PREFERENCE;

  const orderedTypes = isEven
    ? []
    : preferenceOrder.filter((t) => activeCounts[t] > 0);
  for (const name of incubatorTypes.map((it) => it.name)) {
    if (activeCounts[name] > 0 && !orderedTypes.includes(name)) {
      orderedTypes.push(name);
    }
  }
  if (orderedTypes.length === 0) return { error: null };

  const totalUnits = Object.values(activeCounts).reduce((a, b) => a + b, 0);
  const totalCapacity = orderedTypes.reduce(
    (sum, t) => sum + activeCounts[t] * (capacityByType.get(t) ?? 0),
    0,
  );
  if (totalCapacity < totalLabelCount) {
    return {
      error: `Not enough space. ${totalLabelCount} labels need at least ${totalLabelCount} slots but only ${totalCapacity} available.`,
    };
  }

  const standardLabels = labels.filter((l) => categoryFor(l) === 'Standard');
  const day5Labels = labels.filter((l) => categoryFor(l) === 'Day5');
  const exportLabels = labels.filter((l) => categoryFor(l) === 'Export');

  const minIncubatorsNeeded =
    new Set(standardLabels.map(speciesFor)).size +
    new Set(day5Labels.map(speciesFor)).size +
    new Set(exportLabels.map(speciesFor)).size;
  if (minIncubatorsNeeded > totalUnits) {
    return {
      error: `Not enough incubators. ${minIncubatorsNeeded} category/species combinations require separate incubators but only ${totalUnits} available.`,
    };
  }

  const pools: Record<string, Record<Category, Label[]>> = {};
  for (const type of orderedTypes) {
    pools[type] = { Standard: [], Day5: [], Export: [] };
  }
  const lastType = orderedTypes[orderedTypes.length - 1];

  const fillCategory = (categoryLabels: Label[], categoryKey: Category) => {
    let toPlace = categoryLabels;

    if (isEven) {
      // Even mode is validated by the server's proportional-share algorithm; replicating it
      // exactly is unnecessary for a pre-flight check since the total-capacity check above
      // already covers the common failure mode. Fall back to cascade-style placement here,
      // which is a conservative (stricter) approximation.
    }

    for (const type of orderedTypes) {
      if (toPlace.length === 0) break;
      const capacity = capacityByType.get(type) ?? 0;
      const remainingUnitsForType =
        activeCounts[type] -
        Object.values(pools[type]).reduce(
          (sum, arr) => sum + Math.ceil(arr.length / (capacity || 1)),
          0,
        );
      if (capacity <= 0 || remainingUnitsForType <= 0) continue;
      const unitsNeeded = Math.ceil(toPlace.length / capacity);
      if (unitsNeeded <= remainingUnitsForType) {
        pools[type][categoryKey] = pools[type][categoryKey].concat(toPlace);
        toPlace = [];
      } else {
        const fit = remainingUnitsForType * capacity;
        pools[type][categoryKey] = pools[type][categoryKey].concat(
          toPlace.slice(0, fit),
        );
        toPlace = toPlace.slice(fit);
      }
    }
    if (toPlace.length > 0) {
      pools[lastType][categoryKey] =
        pools[lastType][categoryKey].concat(toPlace);
    }
  };

  fillCategory(standardLabels, 'Standard');
  fillCategory(day5Labels, 'Day5');
  fillCategory(exportLabels, 'Export');

  for (const type of orderedTypes) {
    const capacity = capacityByType.get(type) ?? 0;
    const needed =
      unitsNeededFor(pools[type].Standard, capacity) +
      unitsNeededFor(pools[type].Day5, capacity) +
      unitsNeededFor(pools[type].Export, capacity);
    if (needed > activeCounts[type]) {
      return {
        error: `Not enough ${type} incubators. Need ${needed} (Standard / Day 5 / Export and each species cannot share a ${type}) but only ${activeCounts[type]} available.`,
      };
    }
  }

  return { error: null };
};
